import React, {useEffect} from 'react'
import {toast} from 'react-toastify'
import {useLocation} from 'react-router-dom'
import {useOrganizationAuth} from 'organization/Auth/Providers/OrganizationAuthProvider'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import {routes} from 'routes/routes'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'

/**
 * React 18 Strict Mode runs effects twice on mount. The OAuth `code` is single-use — a second
 * `googleCallback` would fail and navigate to login. Share one in-flight promise per callback URL.
 */
const googleOAuthInflight = new Map<string, Promise<boolean>>()

function sharedGoogleOAuthResult(
  search: string,
  run: () => Promise<boolean>,
): Promise<boolean> {
  const existing = googleOAuthInflight.get(search)
  if (existing) {
    return existing
  }
  const p = run()
  googleOAuthInflight.set(search, p)

  return p
}

export function GoogleCallback() {
  const {googleCallback} = useOrganizationAuth()
  const location = useLocation()
  const navigate = useLocalizedNavigate()
  const {t, locale} = useI18n()

  useEffect(() => {
    let cancelled = false
    const search = location.search

    void sharedGoogleOAuthResult(search, () => googleCallback(search)).then(
      (result) => {
        if (cancelled) {
          return
        }
        if (result === true) {
          toast.success(t('auth.loginSuccess'))
          // Full navigation: avoids React SPA races where `user` is not committed yet and
          // guest `<Routes>` `*` sends `/organization/list` → login. Token is already in localStorage.
          const listPath = withLocalePath(locale, routes.organization.list)
          const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
          window.location.replace(
            `${window.location.origin}${publicBase}${listPath}${window.location.hash}`,
          )
          return
        }
        navigate(routes.organization.login, {replace: true})
      },
    )

    return () => {
      cancelled = true
    }
  }, [googleCallback, location.search, locale, navigate, t])

  return <FullPageLoader />
}
